/**
 * Card displaying capacity, attendance, and ticketing information for a hold
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Box,
  Grid,
  TextField,
  IconButton,
} from '@mui/material';
import { People as PeopleIcon, Edit as EditIcon } from '@mui/icons-material';
import type { HoldList, Hold, Show } from 'api-client/models';
import { formatDateLong, isDateInFuture } from '@/utils/dateUtils';
import AdminJson from '@/utilities/AdminJson';
import { useAppContext } from '@/context/AppContext';
import { InlineEditingControls } from '../forms/InlineEditingControls';

interface TicketingAttendanceCardProps {
  hold: HoldList | null;
  holdDetails: Hold | null;
  show?: Show; // Optional since no longer needed
  isEditing: boolean;
  formData?: Record<string, string | number | boolean | null | undefined>;
  onStartEdit: (section: string, holdId: string) => Promise<void>;
  onSave: (section: string) => Promise<void>;
  onCancel: () => void;
  onUpdateField?: (field: string, value: string | number | boolean | null | undefined) => void;
  userTimezone: string;
}

export const TicketingAttendanceCard: React.FC<TicketingAttendanceCardProps> = ({
  hold,
  holdDetails,
  show,
  isEditing,
  formData,
  onStartEdit,
  onSave,
  onCancel,
  onUpdateField,
  userTimezone,
}) => {
  const { isDevMode } = useAppContext();

  if (!hold) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Ticketing & Attendance
          </Typography>
          <Alert severity="info">Select a date above to view ticketing and attendance details.</Alert>
        </CardContent>
      </Card>
    );
  }

  // Determine if this date is in the future
  const isFutureDate = isDateInFuture(hold.holdDate, userTimezone);

  // Get capacity from hold details (show-specific capacity, defaults to stage capacity)
  // Find the stage capacity for comparison (from show.showStages)
  const stageCapacity = show?.showStages?.find(
    (ss) => ss.stage.id === hold.stage
  )?.stage.capacity ?? null;
  
  // Use showCapacity if set, otherwise fall back to stage capacity
  const capacity = holdDetails?.showCapacity ?? stageCapacity ?? 0;
  const attendance = holdDetails?.attendance || 0;
  const ticketsSold = holdDetails?.ticketsSold || 0;
  const capacityUtilization = capacity > 0 ? ((ticketsSold / capacity) * 100).toFixed(1) : '0.0';
  
  // Show note if show capacity differs from stage capacity (and both exist)
  const showCapacityNote = 
    stageCapacity && holdDetails?.showCapacity && holdDetails.showCapacity !== stageCapacity
      ? `Stage capacity: ${stageCapacity}`
      : null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <PeopleIcon />
          <Typography variant="h6" component="span">
            Ticketing & Attendance
          </Typography>
          <Typography variant="body2" color="text.secondary" component="span">
            - {formatDateLong(hold.holdDate)}
          </Typography>
          {!isEditing && (
            <IconButton size="small" color="primary" aria-label="Edit ticketing details"
              onClick={() => void onStartEdit('ticketing', hold.id)}>
              <EditIcon />
            </IconButton>
          )}
        </Stack>

        {isEditing ? (
          // Edit mode
          <Stack spacing={3}>
            {/* Capacity & Attendance Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Capacity & Attendance
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Show Capacity"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData?.showCapacity !== undefined ? formData.showCapacity : capacity}
                    onChange={(e) => onUpdateField?.('showCapacity', e.target.value ? parseInt(e.target.value) : null)}
                    helperText={showCapacityNote || "Maximum number of people for this show"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Actual Attendance"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData?.attendance !== undefined ? formData.attendance : attendance}
                    onChange={(e) => onUpdateField?.('attendance', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={isFutureDate}
                    helperText={isFutureDate ? 'Date is in future' : 'Final attendance count'}
                  />
                </Grid>
              </Grid>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
              <InlineEditingControls onSave={() => onSave('ticketing')} onCancel={onCancel} />
            </Stack>
          </Stack>
        ) : (
          // View mode
          <Grid container spacing={2}>
            <Grid 
              size={{ xs: 6, xl: 3 }}
              onClick={() => void onStartEdit('ticketing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Show Capacity
              </Typography>
              <Typography variant="h6">
                {capacity || 'Not set'}
              </Typography>
              {stageCapacity && !holdDetails?.showCapacity && capacity > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  (from stage)
                </Typography>
              )}
            </Grid>
            <Grid 
              size={{ xs: 6, xl: 3 }}
              onClick={() => void onStartEdit('ticketing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Tickets Sold
              </Typography>
              <Typography variant="h6">
                {ticketsSold}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 6, xl: 3 }}
              onClick={() => void onStartEdit('ticketing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Actual Attendance
              </Typography>
              <Typography variant="h6">
                {isFutureDate ? 'TBD' : attendance || 'Not set'}
              </Typography>
            </Grid>
            <Grid 
              size={{ xs: 6, xl: 3 }}
              onClick={() => void onStartEdit('ticketing', hold.id)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                },
                p: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Capacity Utilization
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: parseFloat(capacityUtilization) >= 80 ? 'statusText.ready' : parseFloat(capacityUtilization) >= 50 ? 'statusText.inFlight' : 'text.primary',
                }}
              >
                {capacityUtilization}%
              </Typography>
            </Grid>
          </Grid>
        )}
      </CardContent>
      <AdminJson isDebug={isDevMode} data={{ holdDetails, hold, formData, show }} />
    </Card>
  );
};
